import {
  Box,
  Button,
  Container,
  Header,
  SpaceBetween,
} from "@amzn/awsui-components-console";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useCart } from "@/contexts/CartContext";
import { useChat } from "@/contexts/ChatContext";
import { useClipPreview } from "@/contexts/ClipPreviewContext";
import { usePanelContent } from "@/contexts/PanelContentContext";
import useCartPreviewItems from "@/hooks/cart/useCartPreviewItems";
import useClearCartModal from "@/hooks/cart/useClearCartModal";
import useProcessCartModal from "@/hooks/cart/useProcessCartModal";
import { CLIP_PREVIEW_DRAWER_ID } from "@/utils/constants";

import EmptyState from "../EmptyState";
import { ICONIK_CART_COLOR, pillButtonStyle } from "../iconik/config";
import IconikClipTable from "../iconik/IconikClipTable";

const Cart: React.FC = () => {
  const navigate = useNavigate();
  const { items: cartItems, removeFromCart } = useCart();
  // The chat the user was on before opening the cart. It survives the /cart
  // route because ChatProvider wraps the router, so an empty string means the
  // origin was a brand-new chat and a non-empty id means an existing thread.
  const { conversationId } = useChat();
  const {
    openPreview,
    registerLatestPlay,
    activeClip,
    isOpen,
    source,
  } = useClipPreview();
  const { setActiveDrawerId } = usePanelContent();

  const { toggle: toggleProcessCart, ModalComponent: ProcessCartModal } =
    useProcessCartModal();
  const { toggle: toggleClearCart, ModalComponent: ClearCartModal } =
    useClearCartModal();

  // Enumerate cart items so they share the chat accordion's "#" column and the
  // Clip Preview's clip counter — and refresh their admin-driven preview
  // fields from each item's play so the cart preview never shows a stale
  // metadata set (cart items are persisted snapshots).
  const enumeratedItems = useCartPreviewItems(cartItems);

  // Keep the cart registered as the "latest" preview so opening the Clip
  // Preview drawer from its own trigger (no row clicked) shows the cart, not a
  // stale chat play.
  useEffect(() => {
    if (enumeratedItems.length > 0) {
      registerLatestPlay({
        clips: enumeratedItems,
        index: 0,
        gameId: "",
        playId: "",
        source: "cart",
      });
    }
  }, [enumeratedItems, registerLatestPlay]);

  // Highlight the row whose clip is open in the preview — but only when the
  // preview belongs to the cart (not a chat play).
  const activeAssetId =
    isOpen && source === "cart" ? activeClip?.assetId : undefined;

  if (cartItems.length === 0) {
    return (
      <Container header={<Header counter="(0)">Clips</Header>}>
        <EmptyState
          title="No clips are in the Cart."
          action={
            <Button
              onClick={() =>
                navigate(
                  conversationId ? `/?thread=${conversationId}` : "/"
                )
              }
            >
              Go back to Chat
            </Button>
          }
        />
      </Container>
    );
  }

  return (
    <SpaceBetween size="s">
      <Container
        header={
          <Header
            counter={`(${cartItems.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="s">
                <Button onClick={toggleClearCart}>Clear</Button>
                <Button variant="primary" onClick={toggleProcessCart}>
                  Process Clips
                </Button>
              </SpaceBetween>
            }
          >
            Clips
          </Header>
        }
      >
        <IconikClipTable
          items={enumeratedItems}
          activeAssetId={activeAssetId}
          actionsHeader="Actions"
          renderActions={(item) => (
            <button
              type="button"
              aria-label="Remove from cart"
              onClick={(e) => {
                e.stopPropagation();
                removeFromCart([item.assetId]);
              }}
              style={{
                ...pillButtonStyle(ICONIK_CART_COLOR),
                background: ICONIK_CART_COLOR,
                color: "#000",
              }}
            >
              Remove
            </button>
          )}
          onRowClick={(_item, index) => {
            openPreview({
              clips: enumeratedItems,
              index,
              gameId: "",
              playId: "",
              source: "cart",
            });
            setActiveDrawerId(CLIP_PREVIEW_DRAWER_ID);
          }}
        />
        <Box float="right" padding={{ top: "xs" }} color="text-status-inactive" fontSize="body-s">
          Click a clip to preview it. Share / Export / Transfer Multiple act on
          the whole cart.
        </Box>
      </Container>
      {ProcessCartModal()}
      {ClearCartModal()}
    </SpaceBetween>
  );
};

export default Cart;
